import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useToast } from '@repo/design-system-kit';
import { useSegment } from '@repo/poly-hooks/segment';
import { useCurrentDevice } from '@repo/shared-kit/hooks';
import { useSentry } from '@repo/shared-kit/sentry';
import { webviewManager } from '@repo/shared-kit/webview';
import { authSdkLoader, initiateGoogleAuth, shouldSendToSentry } from '@repo/shared-kit/utils';
import { getEnvironmentVariables } from '@repo/shared-config/app/helper/environment-variables';
import { getUrlsTable } from '@repo/shared-config/app/constants/hosts';
import { didCancel } from '../../utils/task-cancelation';
import { setAppleSigninUser } from '../../utils/apple-signin-user';
import { AppleAuthButton } from '@repo/shared-kit/auth/components/apple-auth-button';
import { GoogleAuthButton } from './google-auth-button';
import type { AuthButtonsProps, SdkLoadingStatus } from './types';
import styles from './auth-buttons.module.css';

const SDK_STATES_SHOW: SdkLoadingStatus[] = ['loaded', 'not-loaded'];

const env = getEnvironmentVariables();
const { backendUrlsTable } = getUrlsTable(env);
const authBaseURL = backendUrlsTable.authBaseURL;

export function AuthButtons({
  onSignInGoogleSuccess,
  onSignInAppleSuccess,
  onAuthButtonClick,
  className,
}: AuthButtonsProps): ReactNode {
  const intl = useIntl();
  const toast = useToast();
  const segment = useSegment();
  const sentry = useSentry();
  const device = useCurrentDevice();

  const [appleSDKLoadState, setAppleSDKLoadState] = useState<SdkLoadingStatus>('loading');
  const [isAuthenticatingGoogle, setIsAuthenticatingGoogle] = useState(false);
  const [isAuthenticatingApple, setIsAuthenticatingApple] = useState(false);

  // Apple SDK is loaded for Apple devices on iOS 13+. The legacy Ember
  // `deviceManager` exposed `isTablet` + `currentVendor === 'Apple'` —
  // modern iPads still report `os.name === 'iOS'`, so the union check
  // `(isMobile && iOS13+) || isTablet` simplifies to "is iOS and version >= 13".
  const shouldLoadAppleSDK = useMemo(() => {
    const osName = device.os.name;
    const osVersion = device.os.version;

    if (osName !== 'iOS') return false;

    const major = Number(osVersion?.split('.')[0] ?? '0');
    return major >= 13;
  }, [device.os.name, device.os.version]);

  const shouldShowAppleButton = useMemo(() => {
    if (!shouldLoadAppleSDK) {
      return false;
    }
    return SDK_STATES_SHOW.includes(appleSDKLoadState);
  }, [shouldLoadAppleSDK, appleSDKLoadState]);

  useEffect(() => {
    const loadSDK = async (): Promise<void> => {
      if (!shouldLoadAppleSDK) {
        setAppleSDKLoadState('not-loaded');
        return;
      }

      setAppleSDKLoadState('loading');
      try {
        if (!window.AppleID?.auth && env.environment !== 'test') {
          await authSdkLoader.loadAppleSdk();
        }
        setAppleSDKLoadState('loaded');
      } catch {
        setAppleSDKLoadState('not-loaded');
      }
    };

    void loadSDK();
  }, [shouldLoadAppleSDK]);

  const handleAuthProvidersError = useCallback(
    (provider: string, error: Error) => {
      if (error.message === 'popup_closed_by_user') {
        segment.track('authentication_dropped', { authentication_method: provider });
        return;
      }

      segment.track('onboarding_authentication_failed', { authentication_method: provider });
      const message = intl.formatMessage({
        id: 'customer-registration-kit.sign_up.validations.external_auth_generic_issue',
      });
      toast.showToast({ type: 'error', text: message });

      if (shouldSendToSentry(error)) {
        sentry.captureException(error);
      }
    },
    [segment, intl, toast, sentry]
  );

  const handleGoogleSuccess = useCallback(
    async (data: { jwt: string }) => {
      if (isAuthenticatingGoogle) {
        return;
      }

      setIsAuthenticatingGoogle(true);
      try {
        await onSignInGoogleSuccess(data);
      } catch (error) {
        if (!didCancel(error)) {
          throw error;
        }
        // Silently ignore TaskCancelation errors
      } finally {
        setIsAuthenticatingGoogle(false);
      }
    },
    [onSignInGoogleSuccess, isAuthenticatingGoogle]
  );

  const handleAppleSuccess = useCallback(
    async (data: {
      jwt: string;
      code: string;
      user?: {
        name?: {
          firstName: string;
          lastName: string;
        };
        email?: string;
      };
    }) => {
      if (isAuthenticatingApple) {
        return;
      }

      setIsAuthenticatingApple(true);
      try {
        if (data.user) {
          setAppleSigninUser(data.user);
        }
        await onSignInAppleSuccess({ jwt: data.jwt });
      } catch (error) {
        if (!didCancel(error)) {
          throw error;
        }
        // Silently ignore TaskCancelation errors
      } finally {
        setIsAuthenticatingApple(false);
      }
    },
    [onSignInAppleSuccess, isAuthenticatingApple]
  );

  const handleCustomGoogleAuth = useCallback(() => {
    onAuthButtonClick?.('google');

    // The auth popup should be opened only for non-webview users. Auth in webview is handled by Google SDK on mobile
    if (!webviewManager.isWebview) {
      try {
        initiateGoogleAuth({
          authBaseURL,
          callbackOrigin: 'register-js',
          onSuccess: jwt => {
            void handleGoogleSuccess({ jwt });
          },
          onError: error => {
            handleAuthProvidersError('google', error);
          },
        });
      } catch (error) {
        // oxlint-disable-next-line typescript/consistent-type-assertions
        handleAuthProvidersError('google', error as Error);
      }
    }
  }, [onAuthButtonClick, handleGoogleSuccess, handleAuthProvidersError]);

  return (
    <div className={className || styles['auth-buttons']} data-test-auth-button-container>
      <GoogleAuthButton
        onClick={handleCustomGoogleAuth}
        onError={error => {
          handleAuthProvidersError('google', error);
        }}
        onSignInSuccess={handleGoogleSuccess}
        shouldUseWebBridgeBtn
      />
      {shouldShowAppleButton ? (
        <AppleAuthButton
          clientId={env.appleAuth?.clientID}
          onClick={() => {
            segment.track('onboarding_credential_method_selected', {
              authentication_method: 'apple',
            });
            onAuthButtonClick?.('apple');
          }}
          onError={error => {
            handleAuthProvidersError('apple', error);
          }}
          onSignInSuccess={handleAppleSuccess}
          shouldUseInitFailureFallback={appleSDKLoadState === 'not-loaded'}
        >
          {intl.formatMessage({
            id: 'customer-registration-kit.sign_up.create_your_account.cta-apple',
          })}
        </AppleAuthButton>
      ) : null}
    </div>
  );
}
